import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useCallback, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useField } from 'formik';
import { motion } from 'framer-motion';
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill';
import { formatMoney } from 'accounting';
import { TYPOGRAPHY_CLASSNAMES, COLORS } from '../../../../themes';
import { flex, highContrastOutline, isKeyboard, positionAbsolute, TRANSITION_DEFAULT, highContrast } from '../../../../styling';
import StyledContainer from '../../../Styled/StyledContainer';
import Label from '../../Label';
import { useDebounce } from '../../../../hooks';
var Container = /*#__PURE__*/styled(StyledContainer).withConfig({
  displayName: "SliderInput__Container",
  componentId: "sc-hqzgdj-0"
})(["width:100%;box-sizing:border-box;transition:opacity ", ";opacity:", ";"], TRANSITION_DEFAULT, function (_ref) {
  var $opacity = _ref.$opacity;
  return $opacity;
});
var InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "SliderInput__InputWrapper",
  componentId: "sc-hqzgdj-1"
})(["position:relative;width:100%;height:3.2rem;"]);
var NativeTrack = /*#__PURE__*/css(["height:3.2rem;cursor:pointer;"]);
var NativeThumb = /*#__PURE__*/css(["height:3.2rem;width:3.2rem;appearance:none;border-radius:50%;cursor:pointer;"]);
var NativeInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "SliderInput__NativeInput",
  componentId: "sc-hqzgdj-2"
})(["appearance:none;margin:0;border:0;padding:0;outline:none;width:100%;opacity:0;::-webkit-slider-runnable-track{", ";}::-moz-range-track{", ";}::-ms-track{", ";}::-webkit-slider-thumb{", ";}::-moz-range-thumb{", ";}::-ms-thumb{", ";}"], NativeTrack, NativeTrack, NativeTrack, NativeThumb, NativeThumb, NativeThumb);
var Thumb = /*#__PURE__*/styled(motion.div).withConfig({
  displayName: "SliderInput__Thumb",
  componentId: "sc-hqzgdj-3"
})(["", ";z-index:2;box-sizing:border-box;background:", ";border:0.4rem solid ", ";box-shadow:0 4px 4px rgba(0,0,0,0.1);width:3.2rem;border-radius:50%;pointer-events:none;transition:border ", ";", ";", ";"], positionAbsolute(0, 'unset'), COLORS.WHITE, COLORS.SECONDARY_400, TRANSITION_DEFAULT, function (_ref2) {
  var $focus = _ref2.$focus;
  return $focus && css(["background:", ";", ";"], COLORS.SECONDARY_200, highContrastOutline);
}, function (_ref3) {
  var $active = _ref3.$active;
  return $active && css(["border:0.4rem solid ", ";"], COLORS.SECONDARY_500);
});
var ThumbOutline = /*#__PURE__*/styled.div.withConfig({
  displayName: "SliderInput__ThumbOutline",
  componentId: "sc-hqzgdj-4"
})(["", ";border-radius:50%;opacity:0;transition:opacity ", ";", ";"], positionAbsolute(), TRANSITION_DEFAULT, function (_ref4) {
  var $hover = _ref4.$hover,
      $active = _ref4.$active;
  return $hover && !$active && css(["opacity:0.3;box-shadow:0 0 0 8px ", ";"], COLORS.SECONDARY_400);
});
var TrackStyling = /*#__PURE__*/css(["", ";", ";pointer-events:none;> div{width:100%;border-radius:1.2rem;height:0.4rem;overflow:hidden;position:relative;}"], positionAbsolute(), flex());
var TrackBg = /*#__PURE__*/styled.div.withConfig({
  displayName: "SliderInput__TrackBg",
  componentId: "sc-hqzgdj-5"
})(["", ";z-index:0;> div{background:", ";", "}"], TrackStyling, COLORS.NEUTRAL_300, highContrastOutline);
var ActiveTrackBg = /*#__PURE__*/styled.div.withConfig({
  displayName: "SliderInput__ActiveTrackBg",
  componentId: "sc-hqzgdj-6"
})(["", ";z-index:1;"], TrackStyling);
var ActiveTrack = /*#__PURE__*/styled(motion.div).withConfig({
  displayName: "SliderInput__ActiveTrack",
  componentId: "sc-hqzgdj-7"
})(["", ";transform-origin:0 50% 0;background:", ";", "}"], positionAbsolute(), COLORS.SECONDARY_400, highContrast(css(["border:3px solid currentColor;"])));
var MinMaxWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "SliderInput__MinMaxWrapper",
  componentId: "sc-hqzgdj-8"
})(["", ";"], flex('space-between', 'center'));

var _ref7 = /*#__PURE__*/React.createElement(TrackBg, null, /*#__PURE__*/React.createElement("div", null));

var SliderInput = function SliderInput(_ref5) {
  var name = _ref5.name,
      label = _ref5.label,
      _ref5$min = _ref5.min,
      min = _ref5$min === void 0 ? 0 : _ref5$min,
      max = _ref5.max,
      _ref5$step = _ref5.step,
      step = _ref5$step === void 0 ? 100 : _ref5$step,
      className = _ref5.className,
      $css = _ref5.$css;

  var _useResizeDetector = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 0
  }),
      width = _useResizeDetector.width,
      ref = _useResizeDetector.ref;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      position = _useState2[0],
      setPosition = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      focus = _useState4[0],
      setFocus = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      hover = _useState6[0],
      setHover = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      active = _useState8[0],
      setActive = _useState8[1];

  var _useField = useField({
    name: name
  }),
      _useField2 = _slicedToArray(_useField, 3),
      value = _useField2[0].value,
      setValue = _useField2[2].setValue;

  var _useState9 = useState({
    duration: 0
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      transition = _useState10[0],
      setTransition = _useState10[1];

  var _useState11 = useState(0),
      _useState12 = _slicedToArray(_useState11, 2),
      opacity = _useState12[0],
      setOpacity = _useState12[1];

  var deboucedValue = useDebounce(value, 300);
  useEffect(function () {
    // To prevent slider moving during initialization
    var cancelled = false;
    setTimeout(function () {
      if (cancelled) return;
      setOpacity(1);
      setTransition({
        type: 'spring',
        damping: 25,
        stiffness: 300
      });
    }, 300);
    return function () {
      cancelled = true;
    };
  }, []);
  var changePosition = useCallback(function (value) {
    if (width) {
      var tick = (width - 28) / (max - min);
      setPosition(Math.ceil((Number(value) - min) * tick));
    }
  }, [width, max, min]);
  var onChange = useCallback(function (_ref6) {
    var value = _ref6.target.value;
    var newValue = Number(value);
    var maxDiff = max - newValue;
    if (maxDiff < step) newValue = max;
    changePosition(newValue);
    setValue(newValue);
  }, [max, step, changePosition, setValue]);
  useEffect(function () {
    if (deboucedValue) {
      var _value = deboucedValue;
      if (_value < min) _value = min;else if (_value > max) _value = max;
      changePosition(_value);
    }
  }, [deboucedValue, changePosition, min, max]);
  var id = "".concat(name, "-slider");
  return /*#__PURE__*/React.createElement(Container, {
    className: className,
    $css: $css,
    $opacity: opacity,
    "data-test-id": id
  }, /*#__PURE__*/React.createElement(InputWrapper, null, /*#__PURE__*/React.createElement(Label, {
    htmlFor: id,
    hiddenLabel: label
  }), /*#__PURE__*/React.createElement(NativeInput, {
    onFocus: function onFocus(e) {
      return setFocus(isKeyboard(e));
    },
    onBlur: function onBlur() {
      return setFocus(false);
    },
    onMouseEnter: function onMouseEnter() {
      return setHover(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setHover(false);
    },
    onMouseDown: function onMouseDown() {
      return setActive(true);
    },
    onMouseUp: function onMouseUp() {
      return setActive(false);
    },
    ref: ref,
    onChange: onChange,
    type: "range",
    min: min,
    max: max,
    step: step,
    value: value,
    name: name,
    id: id
  }), /*#__PURE__*/React.createElement(Thumb, {
    $focus: focus,
    animate: {
      x: position
    },
    transition: transition,
    $active: active
  }, /*#__PURE__*/React.createElement(ThumbOutline, {
    $hover: hover,
    $focus: focus,
    $active: active
  })), _ref7, /*#__PURE__*/React.createElement(ActiveTrackBg, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ActiveTrack, {
    animate: {
      scaleX: width ? (position + 16) / width : 0
    },
    transition: transition
  })))), /*#__PURE__*/React.createElement(MinMaxWrapper, null, /*#__PURE__*/React.createElement("span", {
    className: TYPOGRAPHY_CLASSNAMES.S
  }, formatMoney(min, undefined, 0)), /*#__PURE__*/React.createElement("span", {
    className: TYPOGRAPHY_CLASSNAMES.S
  }, formatMoney(max, undefined, 0))));
};

export default SliderInput;